// MobileAlertsAck — Phase F of the Client-Surfaces Programme.
// Mobile alert-acknowledge feed. Reuses /api/alerts + /api/alerts/{id}/acknowledge|snooze|escalate.
// Writes go through syncQueue so an offline ack queues and flushes on reconnect.
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft, AlertTriangle, Check, Clock, ArrowUpRight, Loader2, RefreshCw, ShieldAlert
} from 'lucide-react';
import MobileOfflineBanner from './MobileOfflineBanner';
import { queueOrSend } from '../utils/syncQueue';
import { readSharedToken } from '../utils/authTokenKeys';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SEV_TONE = {
  critical: 'bg-danger/10 text-danger border-danger/30',
  high:     'bg-warn/10 text-warn border-warn/30',
  medium:   'bg-primary-50 text-primary-700 border-primary-200',
  low:      'bg-surface-sunken text-ink-muted border-surface-border',
};

const formatWhen = (iso) => {
  if (!iso) return '';
  const mins = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 1) return 'now';
  if (mins < 60) return `${mins}m`;
  if (mins < 60 * 24) return `${Math.floor(mins / 60)}h`;
  return new Date(iso).toLocaleDateString();
};

const MobileAlertsAck = () => {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [orgId, setOrgId] = useState(() => {
    try { return localStorage.getItem('current_organization_id') || null; } catch { return null; }
  });

  // Was localStorage.getItem('token') || localStorage.getItem('mobileToken')
  // — never checked auth_token (audit-2026-09-07 §2, same bug syncQueue.js had).
  const token = readSharedToken();

  // Resolve org id once (PWA also benefits from cached id).
  useEffect(() => {
    if (!token || orgId || !navigator.onLine) return;
    let cancelled = false;
    fetch(`${API_URL}/api/platform/context`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null)
      .then(ctx => {
        if (cancelled || !ctx) return;
        const id = ctx?.organizations?.[0]?.id;
        if (id) {
          setOrgId(id);
          try { localStorage.setItem('current_organization_id', id); } catch { /* ignore */ }
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [token, orgId]);

  const headers = useMemo(() => ({
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(orgId ? { 'X-Organization-Id': orgId } : {}),
  }), [token, orgId]);

  const reload = useCallback(async () => {
    if (!orgId || !navigator.onLine) { setLoading(false); return; }
    setLoading(true);
    try {
      const params = new URLSearchParams();
      ['open', 'acknowledged', 'in_progress', 'resolved'].forEach(s => params.append('status', s));
      params.set('limit', '30');
      const res = await fetch(`${API_URL}/api/alerts?${params.toString()}`, { headers });
      const list = res.ok ? await res.json() : [];
      // Prefer alerts assigned to this user when the payload includes assigned_to.
      const mine = Array.isArray(list)
        ? list.filter((a) => {
            const assignees = a.assigned_to || [];
            if (!assignees.length) return true;
            try {
              const uid = JSON.parse(localStorage.getItem('user') || '{}').id;
              return !uid || assignees.includes(uid);
            } catch {
              return true;
            }
          })
        : [];
      setAlerts(mine);
      try { localStorage.setItem('mobileAlertsCache', JSON.stringify(list.slice(0, 30))); } catch { /* ignore */ }
    } catch {
      // Try cache for offline rendering
      try {
        const cached = JSON.parse(localStorage.getItem('mobileAlertsCache') || '[]');
        setAlerts(cached);
      } catch { /* ignore */ }
    } finally { setLoading(false); }
  }, [orgId, headers]);

  useEffect(() => {
    if (!orgId) {
      // Fall back to cached list when offline / no org yet
      try {
        const cached = JSON.parse(localStorage.getItem('mobileAlertsCache') || '[]');
        if (Array.isArray(cached) && cached.length) setAlerts(cached);
      } catch { /* ignore */ }
      setLoading(false);
      return;
    }
    reload();
  }, [orgId, reload]);

  const act = async (alertId, action, body) => {
    setBusyId(`${alertId}:${action}`);
    // Optimistic update — flip the local card immediately
    setAlerts(prev => prev.map(a => a.id === alertId ? {
      ...a,
      status:
        action === 'acknowledge' ? 'acknowledged'
          : action === 'snooze' ? 'snoozed'
            : action === 'resolve' ? 'resolved'
              : action === 'close' ? 'closed'
                : a.status,
    } : a));
    const res = await queueOrSend({
      method: 'POST',
      path: `/api/alerts/${alertId}/${action}`,
      headers,
      body: body || {},
    });
    setBusyId(null);
    if (!res.ok && !res.queued) {
      // Revert optimistic update on hard failure
      reload();
    }
  };

  return (
    <div className="min-h-screen bg-surface-muted" data-testid="mobile-alerts-page">
      <MobileOfflineBanner />
      <header className="bg-surface border-b border-surface-border px-4 py-3 flex items-center gap-3">
        <Link to="/mobile/dashboard" className="p-2 -ml-2 rounded-md hover:bg-surface-sunken" data-testid="mobile-alerts-back">
          <ArrowLeft className="w-4 h-4" />
        </Link>
        <div className="flex-1">
          <div className="text-xs uppercase tracking-overline text-ink-muted">Stay Compliant</div>
          <h1 className="font-display text-lg text-ink flex items-center gap-2"><ShieldAlert className="w-4 h-4 text-primary-500" /> Alerts</h1>
        </div>
        <button onClick={reload} className="p-2 rounded-md hover:bg-surface-sunken" data-testid="mobile-alerts-refresh">
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </header>

      <div className="px-4 py-4 space-y-3 pb-24">
        {loading ? (
          <div className="text-center text-ink-muted py-10"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>
        ) : alerts.length === 0 ? (
          <div className="bg-surface border border-surface-border rounded-lg p-8 text-center" data-testid="mobile-alerts-empty">
            <ShieldAlert className="w-10 h-10 mx-auto text-ink-subtle mb-3" />
            <p className="font-display text-base text-ink">All clear on your shift</p>
            <p className="text-xs text-ink-muted mt-1">No open or acknowledged alerts right now.</p>
          </div>
        ) : (
          alerts.map(a => {
            const tone = SEV_TONE[a.severity] || SEV_TONE.low;
            const ackBusy = busyId === `${a.id}:acknowledge`;
            const snoozeBusy = busyId === `${a.id}:snooze`;
            const escBusy = busyId === `${a.id}:escalate`;
            const resolveBusy = busyId === `${a.id}:resolve`;
            const isAcked = a.status === 'acknowledged' || a.status === 'resolved' || a.status === 'closed';
            return (
              <div key={a.id} className="bg-surface border border-surface-border rounded-lg p-4 shadow-ambient" data-testid={`mobile-alert-card-${a.id}`}>
                <div className="flex items-start gap-3">
                  <span className={`text-xs px-2 py-0.5 rounded-md border ${tone} font-medium`} data-testid={`mobile-alert-card-${a.id}-severity`}>
                    {a.severity}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-ink">{a.title}</p>
                    {a.description ? <p className="text-xs text-ink-muted mt-0.5 line-clamp-2">{a.description}</p> : null}
                    <p className="text-[10px] uppercase tracking-wide text-ink-muted mt-1">{a.status}</p>
                  </div>
                  <span className="text-xs text-ink-muted tabular-nums" title={a.created_at}>
                    <Clock className="w-3 h-3 inline mr-0.5" />{formatWhen(a.created_at)}
                  </span>
                </div>
                <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-4">
                  <button
                    disabled={isAcked || ackBusy}
                    onClick={() => act(a.id, 'acknowledge', { notes: 'Acked on mobile' })}
                    className="px-3 py-2 text-xs rounded-md bg-primary-500 text-surface hover:bg-primary-600 disabled:opacity-40"
                    data-testid={`mobile-alert-ack-${a.id}`}
                  >
                    {ackBusy ? <Loader2 className="w-3.5 h-3.5 inline animate-spin" /> : <Check className="w-3.5 h-3.5 inline mr-1" />}
                    Ack
                  </button>
                  <button
                    disabled={a.status === 'resolved' || a.status === 'closed' || resolveBusy}
                    onClick={() => act(a.id, 'resolve', { action_taken: 'Resolved on mobile' })}
                    className="px-3 py-2 text-xs rounded-md bg-emerald-700 text-white hover:bg-emerald-800 disabled:opacity-40"
                    data-testid={`mobile-alert-resolve-${a.id}`}
                  >
                    {resolveBusy ? <Loader2 className="w-3.5 h-3.5 inline animate-spin" /> : <Check className="w-3.5 h-3.5 inline mr-1" />}
                    Resolve
                  </button>
                  <button
                    disabled={a.status === 'snoozed' || snoozeBusy}
                    onClick={() => act(a.id, 'snooze', { snooze_until: new Date(Date.now() + 15 * 60 * 1000).toISOString(), reason: '15m snooze' })}
                    className="px-3 py-2 text-xs rounded-md border border-surface-border hover:bg-surface-sunken disabled:opacity-40"
                    data-testid={`mobile-alert-snooze-${a.id}`}
                  >
                    {snoozeBusy ? <Loader2 className="w-3.5 h-3.5 inline animate-spin" /> : <Clock className="w-3.5 h-3.5 inline mr-1" />}
                    15m
                  </button>
                  <button
                    disabled={escBusy}
                    onClick={() => act(a.id, 'escalate', { reason: 'Manual mobile escalate' })}
                    className="px-3 py-2 text-xs rounded-md border border-warn/40 text-warn hover:bg-warn/5 disabled:opacity-40"
                    data-testid={`mobile-alert-escalate-${a.id}`}
                  >
                    {escBusy ? <Loader2 className="w-3.5 h-3.5 inline animate-spin" /> : <ArrowUpRight className="w-3.5 h-3.5 inline mr-1" />}
                    Escalate
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default MobileAlertsAck;
